import { request, streamRequest, uploadRequest, downloadRequest } from './httpClient';
import type {
  CustomNode,
  CustomNodeWhatsNewEntry,
  DiscussionMessage,
  Flow,
  FlowInput,
  FlowStatus,
  FlowPreview,
  FlowNodeRequest,
  PaginatedFlowNodeRequests,
  TagSummary,
  User,
  ForumThread,
  ForumPost,
  PaginatedForumThreads,
  ForumThreadStatus,
  ForumCategory,
  AdminDashboardData,
  DashboardEventSummary,
  UserActivityDetail,
  NotificationPreferences,
  Team,
  TeamMember,
  UserSummary,
  ApiKey,
  ApiKeyCreateResponse,
  Report
} from '../types';

export interface LoginResponse {
  token: string;
  user: User;
  idToken?: string | null;
  accessToken?: string | null;
  claims?: Record<string, unknown>;
}

interface FlowQuery {
  search?: string;
  tags?: string[];
  status?: FlowStatus | 'all';
  kind?: 'flow' | 'node';
  author?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  ownerId?: string;
  countOnly?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'upvotes';
  sortOrder?: 'asc' | 'desc';
  recommendedOnly?: boolean;
  madeByThales?: boolean;
  allTeams?: boolean;
  visibility?: 'public' | 'teams';
}

interface ForumThreadsQuery {
  search?: string;
  authorSearch?: string;
  status?: ForumThreadStatus | 'all';
  tag?: string;
  createdBy?: string;
  answered?: boolean;
  pinned?: boolean;
  categoryId?: string;
  page?: number;
  pageSize?: number;
}

interface ForumRequestQuery {
  sort?: 'upvotes' | 'recent';
  kind?: 'flow' | 'node';
  search?: string;
  hideDone?: 'true';
  status?: 'active' | 'completed' | 'done';
  createdBy?: 'me';
  page?: number;
  pageSize?: number;
}

type AnalyticsEventType = 'flow_download' | 'flow_copy';

interface AnalyticsEventInput {
  type: AnalyticsEventType;
  flowId: string;
  metadata?: Record<string, unknown>;
}

interface AccountPreferencesResponse {
  notificationPreferences: NotificationPreferences;
}

interface AccountPreferencesUpdate {
  notificationPreferences: {
    marketplace?: Partial<NotificationPreferences['marketplace']>;
    forum?: Partial<NotificationPreferences['forum']>;
  };
}

interface TeamMembersResponse {
  team: Team;
  members: TeamMember[];
}

// --- Condition Engine Types ---

export interface ConditionAtom {
  target: string;
  op: 'exists' | 'not_exists' | 'equals' | 'not_equals' | 'matches' | 'not_matches' |
      'contains' | 'starts_with' | 'in' | 'not_in' | 'typeof';
  value?: string | number | boolean | string[];
  flags?: string;
}

export interface ConditionGroup {
  logic: 'and' | 'or';
  conditions: Array<ConditionAtom | ConditionGroup>;
}

export type Condition = ConditionAtom | ConditionGroup;

// --- Validator Rule Types ---

export interface ValidatorRule {
  id: string;
  name: string;
  description: string;
  severity: 'error' | 'warning' | 'info' | 'critical';
  enabled: boolean;
  type: 'handler' | 'condition' | 'pattern';
  // Code bodies (JS function body strings — executed via new Function())
  checkBody?: string;
  fixBody?: string;
  fixAllBody?: string;
  canFixBody?: string;
  // Condition rules
  nodeNameFilter?: string | null;
  nodeNamePattern?: string | null;
  conditions?: Condition;
  message?: string;
  // Pattern rules (legacy)
  match?: {
    target: string;
    pattern: string;
    flags?: string;
    negate?: boolean;
    nodeNameFilter?: string | null;
  };
}

// Legacy types (kept for backward compat)
export interface ValidatorPatternRule {
  id: string;
  name: string;
  description: string;
  severity: 'error' | 'warning' | 'info';
  enabled: boolean;
  match?: {
    target: string;
    pattern: string;
    flags?: string;
    negate?: boolean;
    nodeNameFilter?: string | null;
  };
  conditions?: Condition;
  nodeNameFilter?: string | null;
  nodeNamePattern?: string | null;
  message: string;
}

export interface ValidatorConfig {
  version?: string | null;
  rules?: ValidatorRule[];
  // Legacy fields
  ruleOverrides?: Array<{
    id: string;
    enabled?: boolean;
    severity?: string;
    description?: string;
  }>;
  patternRules?: ValidatorPatternRule[];
}

function buildForumThreadsQuery(params: ForumThreadsQuery) {
  const query = new URLSearchParams();
  if (params.search) {
    query.append('search', params.search);
  }
  if (params.status && params.status !== 'all') {
    query.append('status', params.status);
  }
  if (params.tag) {
    query.append('tag', params.tag);
  }
  if (params.authorSearch) {
    query.append('authorSearch', params.authorSearch);
  }
  if (params.createdBy) {
    query.append('createdBy', params.createdBy);
  }
  if (typeof params.answered === 'boolean') {
    query.append('answered', params.answered ? 'true' : 'false');
  }
  if (params.pinned) {
    query.append('pinned', 'true');
  }
  if (params.categoryId) {
    query.append('categoryId', params.categoryId);
  }
  if (typeof params.page === 'number') {
    query.append('page', String(Math.max(Math.floor(params.page), 1)));
  }
  if (typeof params.pageSize === 'number') {
    query.append('pageSize', String(Math.max(Math.floor(params.pageSize), 1)));
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

function buildQuery(params: FlowQuery) {
  const query = new URLSearchParams();
  if (params.search) {
    query.append('search', params.search);
  }
  if (params.status) {
    query.append('status', params.status);
  }
  if (params.tags && params.tags.length > 0) {
    query.append('tags', params.tags.join(','));
  }
  if (params.kind) {
    query.append('kind', params.kind);
  }
  if (params.author) {
    query.append('author', params.author);
  }
  if (params.createdFrom) {
    query.append('createdFrom', params.createdFrom);
  }
  if (params.createdTo) {
    query.append('createdTo', params.createdTo);
  }
  if (params.updatedFrom) {
    query.append('updatedFrom', params.updatedFrom);
  }
  if (params.updatedTo) {
    query.append('updatedTo', params.updatedTo);
  }
  if (params.ownerId) {
    query.append('ownerId', params.ownerId);
  }
  if (params.recommendedOnly) {
    query.append('recommended', 'true');
  }
  if (params.madeByThales) {
    query.append('madeByThales', 'true');
  }
  if (params.allTeams) {
    query.append('allTeams', 'true');
  }
  if (params.countOnly) {
    query.append('count', 'true');
  }
  if (typeof params.page === 'number' && Number.isFinite(params.page)) {
    query.append('page', String(Math.max(Math.floor(params.page), 1)));
  }
  if (typeof params.pageSize === 'number' && Number.isFinite(params.pageSize)) {
    query.append('pageSize', String(Math.max(Math.floor(params.pageSize), 1)));
  }
  if (params.sortBy) {
    query.append('sortBy', params.sortBy);
  }
  if (params.sortOrder) {
    query.append('sortOrder', params.sortOrder);
  }
  if (params.allTeams) {
    query.append('allTeams', 'true');
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

function buildForumRequestQuery(params: ForumRequestQuery = {}) {
  const query = new URLSearchParams();
  if (params.sort) {
    query.append('sort', params.sort);
  }
  if (params.kind) {
    query.append('kind', params.kind);
  }
  if (params.search) {
    query.append('search', params.search);
  }
  if (params.hideDone) {
    query.append('hideDone', params.hideDone);
  }
  if (params.status) {
    query.append('status', params.status);
  }
  if (params.createdBy) {
    query.append('createdBy', params.createdBy);
  }
  if (typeof params.page === 'number') {
    query.append('page', String(Math.max(1, Math.floor(params.page))));
  }
  if (typeof params.pageSize === 'number') {
    query.append('pageSize', String(Math.max(1, Math.floor(params.pageSize))));
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

export interface PaginatedFlowsResponse {
  items: Flow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const api = {
  currentUser: () => request<User>('/auth/me'),
  accountPreferences: () => request<AccountPreferencesResponse>('/account/preferences'),
  updateAccountPreferences: (payload: AccountPreferencesUpdate) =>
    request<AccountPreferencesResponse>('/account/preferences', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }),
  deleteAccount: () =>
    request<{ success: boolean }>('/account', {
      method: 'DELETE'
    }),
  listApiKeys: () => request<{ keys: ApiKey[] }>('/account/api-keys'),
  createApiKey: (name: string) =>
    request<ApiKeyCreateResponse>('/account/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name })
    }),
  revokeApiKey: (id: string) =>
    request<{ revoked: boolean }>(`/account/api-keys/${id}`, {
      method: 'DELETE'
    }),
  restoreDeletedAccount: (token: string) =>
    request<{ restored: boolean; login?: LoginResponse }>('/auth/restore', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ token })
    }),
  flows: (params: FlowQuery = {}) => request<Flow[]>(`/flows${buildQuery(params)}`),
  paginatedFlows: (params: FlowQuery = {}) =>
    request<PaginatedFlowsResponse>(`/flows${buildQuery(params)}`),
  flowCount: async (params: FlowQuery = {}) => {
    const response = await request<{ count: number }>(
      `/flows${buildQuery({ ...params, countOnly: true })}`
    );
    return response.count;
  },
  flow: (id: string) => request<Flow>(`/flows/${id}`),
  exportFlow: (id: string) => request<unknown>(`/flows/${id}/export`),
  recordAnalyticsEvent: (payload: AnalyticsEventInput) =>
    request<{ recorded: boolean }>('/analytics/events', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  watchFlow: (id: string) =>
    request<{ watching: boolean }>(`/flows/${id}/watch`, {
      method: 'POST'
    }),
  unwatchFlow: (id: string) =>
    request<{ watching: boolean }>(`/flows/${id}/watch`, {
      method: 'DELETE'
    }),
  createFlow: (payload: FlowInput) =>
    request<Flow>('/flows', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  updateFlow: (id: string, payload: Partial<FlowInput>) =>
    request<Flow>(`/flows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    }),
  approveFlow: (id: string, notes?: string) =>
    request<Flow>(`/flows/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes })
    }),
  unapproveFlow: (
    id: string,
    options: { notes?: string; restoreApprovedSnapshot?: boolean } = {}
  ) =>
    request<Flow>(`/flows/${id}/unapprove`, {
      method: 'POST',
      body: JSON.stringify(options)
    }),
  rejectFlow: (
    id: string,
    options: { notes?: string; deleteAfterReject?: boolean } = {}
  ) =>
    request<Flow | void>(`/flows/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(options)
    }),
  deleteFlow: (id: string) =>
    request<void>(`/flows/${id}`, { method: 'DELETE' }),
  restoreFlow: (id: string) =>
    request<{ restored: boolean; id: string; title: string }>(`/flows/${id}/restore`, { method: 'POST' }),
  deletedFlows: () =>
    request<Array<{
      id: string;
      title: string;
      kind: string;
      status: string;
      deletedAt: string;
      deletedBy: string;
      deletedByName: string;
      daysRemaining: number;
    }>>('/admin/deleted-flows'),
  addDiscussionComment: (id: string, message: string) =>
    request<DiscussionMessage>(`/flows/${id}/discussion`, {
      method: 'POST',
      body: JSON.stringify({ message })
    }),
  updateDiscussionComment: (id: string, discussionId: string, message: string) =>
    request<DiscussionMessage>(`/flows/${id}/discussion/${discussionId}`, {
      method: 'PUT',
      body: JSON.stringify({ message })
    }),
  deleteDiscussionComment: (id: string, discussionId: string) =>
    request<void>(`/flows/${id}/discussion/${discussionId}`, {
      method: 'DELETE'
    }),
  upvoteFlow: (id: string) =>
    request<{ upvoteCount: number; viewerHasUpvoted: boolean }>(`/flows/${id}/upvote`, {
      method: 'POST'
    }),
  removeFlowUpvote: (id: string) =>
    request<{ upvoteCount: number; viewerHasUpvoted: boolean }>(`/flows/${id}/upvote`, {
      method: 'DELETE'
    }),
  recommendFlow: (id: string) =>
    request<Flow>(`/flows/${id}/recommend`, {
      method: 'POST'
    }),
  clearRecommendation: (id: string) =>
    request<Flow>(`/flows/${id}/recommend`, {
      method: 'DELETE'
    }),
  markMadeByThales: (id: string) =>
    request<Flow>(`/flows/${id}/made-by-thales`, {
      method: 'POST'
    }),
  clearMadeByThales: (id: string) =>
    request<Flow>(`/flows/${id}/made-by-thales`, {
      method: 'DELETE'
    }),
  forumThreads: (params: ForumThreadsQuery = {}) =>
    request<PaginatedForumThreads>(`/forum/threads${buildForumThreadsQuery(params)}`),
  createForumThread: (payload: {
    title: string;
    content: string;
    tags?: string[];
    categoryId: string;
    isPinned?: boolean;
    isLocked?: boolean;
    lockReason?: string | null;
  }) =>
    request<ForumThread>('/forum/threads', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  forumCategories: () => request<ForumCategory[]>('/forum/categories'),
  forumRequests: (params: ForumRequestQuery = {}) =>
    request<PaginatedFlowNodeRequests>(`/forum/requests${buildForumRequestQuery(params)}`),
  createFlowNodeRequest: (payload: { title: string; description: string }) =>
    request<FlowNodeRequest>('/forum/requests', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  updateFlowNodeRequest: (id: string, payload: { title?: string; description?: string }) =>
    request<FlowNodeRequest>(`/forum/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }),
  withdrawFlowNodeRequest: (id: string) =>
    request<{ success: boolean }>(`/forum/requests/${id}/withdraw`, {
      method: 'POST'
    }),
  markFlowNodeRequestStatus: (
    id: string,
    payload: { status: 'open' | 'under_development' | 'done'; marketplaceLink?: string }
  ) =>
    request<FlowNodeRequest>(`/forum/requests/${id}/status`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  markFaqVisited: () =>
    request<{ visited: boolean }>('/faq/visit', {
      method: 'POST'
    }),
  upvoteFlowNodeRequest: (id: string) =>
    request<{ upvoteCount: number; viewerHasUpvoted: boolean }>(`/forum/requests/${id}/upvote`, {
      method: 'POST'
    }),
  removeFlowNodeRequestUpvote: (id: string) =>
    request<{ upvoteCount: number; viewerHasUpvoted: boolean }>(`/forum/requests/${id}/upvote`, {
      method: 'DELETE'
    }),
  forumThread: (id: string) => request<ForumThread>(`/forum/threads/${id}`),
  createForumPost: (threadId: string, payload: { content: string; parentId?: string | null }) =>
    request<ForumThread>(`/forum/threads/${threadId}/posts`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  updateForumPost: (postId: string, payload: { content?: string; moveToThreadId?: string }) =>
    request<ForumPost>(`/forum/posts/${postId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }),
  updateForumThreadCategory: (threadId: string, categoryId: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/category`, {
      method: 'PATCH',
      body: JSON.stringify({ categoryId })
    }),
  deleteForumPost: (postId: string) =>
    request<ForumPost>(`/forum/posts/${postId}`, {
      method: 'DELETE'
    }),
  pinForumThread: (threadId: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/pin`, {
      method: 'POST'
    }),
  unpinForumThread: (threadId: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/pin`, {
      method: 'DELETE'
    }),
  lockForumThread: (threadId: string, reason?: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/lock`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null })
    }),
  unlockForumThread: (threadId: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/lock`, {
      method: 'DELETE'
    }),
  watchForumThread: (threadId: string) =>
    request<{ watching: boolean }>(`/forum/threads/${threadId}/watch`, {
      method: 'POST'
    }),
  unwatchForumThread: (threadId: string) =>
    request<{ watching: boolean }>(`/forum/threads/${threadId}/watch`, {
      method: 'DELETE'
    }),
  markForumAnswer: (threadId: string, postId: string | null) =>
    request<ForumThread>(`/forum/threads/${threadId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ postId })
    }),
  closeForumThread: (threadId: string, reason?: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/close`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null })
    }),
  reopenForumThread: (threadId: string) =>
    request<ForumThread>(`/forum/threads/${threadId}/reopen`, {
      method: 'POST'
    }),
  flowPreview: (id: string) => request<FlowPreview>(`/flows/${id}/preview`),
  tags: () => request<TagSummary[]>('/tags'),
  adminDashboard: () => request<AdminDashboardData>('/admin/dashboard'),
  adminUserActivity: (userId: string) => request<UserActivityDetail>(`/admin/users/${userId}/activity`),
  adminDayActivity: (date: string) =>
    request<DashboardEventSummary[]>(`/admin/activity-events?date=${encodeURIComponent(date)}`),
  adminFeatureFlags: () =>
    request<{ availableFlags: string[]; users: Array<{ id: string; name: string; email: string; featureFlags: string[] }> }>(
      '/admin/feature-flags'
    ),
  adminAddFeatureFlag: (userId: string, flag: string) =>
    request<{ message: string }>(`/admin/users/${userId}/feature-flags`, {
      method: 'POST',
      body: JSON.stringify({ flag })
    }),
  adminRemoveFeatureFlag: (userId: string, flag: string) =>
    request<{ message: string }>(`/admin/users/${userId}/feature-flags/${encodeURIComponent(flag)}`, {
      method: 'DELETE'
    }),
  myTeams: () => request<{ teams: Team[] }>('/me/teams'),
  listTeams: (params?: { status?: string }) =>
    request<Team[]>(`/teams${params?.status ? `?status=${encodeURIComponent(params.status)}` : ''}`),
  approveTeam: (teamId: string) => request<Team>(`/teams/${teamId}/approve`, { method: 'POST' }),
  createTeam: (payload: { name: string; description?: string; region?: string }) =>
    request<Team>('/teams', { method: 'POST', body: JSON.stringify(payload) }),
  teamMembers: (teamId: string) => request<TeamMembersResponse>(`/teams/${teamId}/members`),
  addTeamMember: (
    teamId: string,
    payload: { userId?: string; email?: string; role?: 'admin' | 'member' }
  ) =>
    request<{ id: string; teamId: string; userId: string; role: 'admin' | 'member' }>(
      `/teams/${teamId}/members`,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    ),
  removeTeamMember: (teamId: string, userId: string) =>
    request<{ removed: boolean }>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  searchUsers: (query: string) =>
    request<UserSummary[]>(`/users/search?q=${encodeURIComponent(query)}`),
  updateTeam: (teamId: string, payload: { name?: string; description?: string | null; region?: string | null }) =>
    request<Team>(`/teams/${teamId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  oidcAuthorize: () =>
    request<{ authorizationUrl: string }>('/auth/oidc/login', { skipAuth: true }),
  oidcCallback: (code: string, state: string) =>
    request<LoginResponse>('/auth/oidc/callback', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ code, state })
    }),
  oidcMetadata: () =>
    request<{ endSessionEndpoint: string | null }>('/auth/oidc/metadata', { skipAuth: true }),

  // Reports
  reports: (params?: { status?: string }) =>
    request<Report[]>(
      `/reports${params?.status ? `?status=${encodeURIComponent(params.status)}` : ''}`
    ),
  report: (id: string) => request<Report>(`/reports/${id}`),
  updateReport: (id: string, payload: { status?: string; adminNotes?: string }) =>
    request<Report>(`/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    }),
  openReportCount: () => request<{ count: number }>('/reports/count'),

  // AI Designer
  aiChat: (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    onChunk: (content: string) => void,
    onDone: () => void,
    onError: (error: Error) => void
  ) => streamRequest('/ai/chat', { messages }, onChunk, onDone, onError),
  getAiConfig: () =>
    request<{ provider: string; requiresUserAuth: boolean }>('/ai/config'),
  getGeminiOAuthUrl: () =>
    request<{ url: string }>('/oauth/google/authurl'),
  getGeminiStatus: () =>
    request<{ connected: boolean; email: string | null; connectedAt: string | null }>('/oauth/google/status'),
  disconnectGemini: () =>
    request<{ success: boolean }>('/oauth/google/disconnect', { method: 'POST' }),

  // Extension
  getExtensionStoreVersion: () =>
    request<{ version: string | null; status: string | null }>('/extension/store-version'),
  getRuleChangelog: () =>
    request<{ entries: { version: string; date: string; items: string[] }[] }>('/extension/rule-changelog'),

  // Validator Config
  getValidatorConfig: () =>
    request<ValidatorConfig>('/admin/validator-config'),
  updateValidatorConfig: (config: ValidatorConfig) =>
    request<{ success: boolean; version: string }>('/admin/validator-config', {
      method: 'PUT',
      body: JSON.stringify(config)
    }),
  seedValidatorConfig: () =>
    request<{ success: boolean; version: string; ruleCount: number }>('/admin/validator-config/seed', {
      method: 'POST'
    }),

  // Validation Health
  getValidationHealth: () =>
    request<ValidationHealthData>('/admin/validation-health'),

  // Custom Nodes
  customNodes: () => request<CustomNode[]>('/custom-nodes'),
  customNodesWhatsNew: () => request<CustomNodeWhatsNewEntry[]>('/custom-nodes/whats-new'),
  customNode: (id: string) => request<CustomNode>(`/custom-nodes/${id}`),
  createCustomNode: (formData: FormData) =>
    uploadRequest<CustomNode>('/custom-nodes', formData),
  updateCustomNode: (id: string, formData: FormData) =>
    uploadRequest<CustomNode>(`/custom-nodes/${id}`, formData, { method: 'PATCH' }),
  deleteCustomNode: (id: string) =>
    request<void>(`/custom-nodes/${id}`, { method: 'DELETE' }),
  downloadCustomNode: (id: string) => downloadRequest(`/custom-nodes/${id}/download`)
};

export interface ValidationHealthData {
  topRules: { ruleId: string; count: number }[];
  byDomain: { domainId: string; flowCount: number; issueCount: number }[];
  timeline: { date: string; errors: number; warnings: number; summaries: number }[];
  totals: { summaries: number; domains: number; uniqueFlows: number };
}
